package cc.bluepeak.clubmanager.site.components.layouts

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import cc.bluepeak.clubmanager.site.Res
import cc.bluepeak.clubmanager.site.styles.ButtonLinkVariant
import cc.bluepeak.clubmanager.site.styles.CardStyle
import cc.bluepeak.clubmanager.site.styles.NavLinkVariant
import cc.bluepeak.clubmanager.site.styles.Theme
import cc.bluepeak.clubmanager.site.styles.scrollbarColor
import com.varabyte.kobweb.browser.dom.ElementTarget
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.boxShadow
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.gridRow
import com.varabyte.kobweb.compose.ui.modifiers.gridTemplateRows
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.zIndex
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.data.getValue
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.icons.lucide.LucideCalendar
import com.varabyte.kobweb.silk.components.icons.lucide.LucideClipboardClock
import com.varabyte.kobweb.silk.components.icons.lucide.LucideReceiptEuro
import com.varabyte.kobweb.silk.components.icons.lucide.LucideUsers
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.overlay.Popover
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.document
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.fr
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Text

data class PageLayoutData(
    val title: String,
    val description: String,
)

val PageStyle = CssStyle {
    base { Modifier.fillMaxSize().padding(leftRight = 1.cssRem) }
    Breakpoint.MD { Modifier.fillMaxWidth(80.percent).padding(0.px) }
}

@Layout
@Composable
fun PageLayout(ctx: PageContext, content: @Composable () -> Unit) {
    val data = ctx.data.getValue<PageLayoutData>()
    LaunchedEffect(data.title) {
        document.title = data.title
        document.querySelector("""meta[name="og:title"]""")?.setAttribute("content", data.title)
        document.querySelector("""meta[name="description"]""")?.setAttribute("content", data.description)
        document.querySelector("""meta[name="og:description"]""")?.setAttribute("content", data.description)
        document.body?.style?.setProperty("--${scrollbarColor.name}", Theme.primary.toString())
        document.querySelector("""link[rel="icon"]""")?.setAttribute("href", "/favicon.ico")
    }
    Box(
        Modifier.id("page")
            .fillMaxWidth()
            .minHeight(100.percent)
            .gap(2.cssRem)
            .gridTemplateRows { size(1.fr); size(minContent) },
        contentAlignment = Alignment.Center
    ) {
        Column(
            Modifier.fillMaxSize().gridRow(1),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            NavHeader(
                {
                    Link(Res.link.home, Res.string.home, Modifier.onClick { it() }, NavLinkVariant)
                    Link("#", Res.string.features, variant = NavLinkVariant)
                    Popover(
                        ElementTarget.PreviousSibling,
                        offsetPixels = 0,
                        hideDelayMs = 200,
                        modifier = Modifier.zIndex(9999999).padding(1.cssRem)
                    ) {
                        SimpleGrid(
                            numColumns(1, 2), modifier = CardStyle.toModifier().padding(1.cssRem)
                                .boxShadow(0.px, 0.px, 15.px, 0.px, Color.argb(70, 0, 0, 0))
                                .gap(1.cssRem)
                        ) {
                            Link(Res.link.memberAdministration, Modifier.onClick { it() }, NavLinkVariant) {
                                Row(
                                    verticalAlignment = Alignment.CenterVertically,
                                    horizontalArrangement = Arrangement.spacedBy(.5.cssRem)
                                ) {
                                    LucideUsers()
                                    Text(Res.string.member_admin_tag)
                                }
                            }
                            Link(Res.link.squadManagement, Modifier.onClick { it() }, NavLinkVariant) {
                                Row(
                                    verticalAlignment = Alignment.CenterVertically,
                                    horizontalArrangement = Arrangement.spacedBy(.5.cssRem)
                                ) {
                                    LucideClipboardClock()
                                    Text(Res.string.squad_mgmt_tag)
                                }
                            }
                            Link(Res.link.calendar, Modifier.onClick { it() }, NavLinkVariant) {
                                Row(
                                    verticalAlignment = Alignment.CenterVertically,
                                    horizontalArrangement = Arrangement.spacedBy(.5.cssRem)
                                ) {
                                    LucideCalendar()
                                    Text(Res.string.calendar_tag)
                                }
                            }
                            Link(Res.link.invoicing, Modifier.onClick { it() }, NavLinkVariant) {
                                Row(
                                    verticalAlignment = Alignment.CenterVertically,
                                    horizontalArrangement = Arrangement.spacedBy(.5.cssRem)
                                ) {
                                    LucideReceiptEuro()
                                    Text(Res.string.invoicing_tag)
                                }
                            }
                        }
                    }
//                    Link(Res.link.pricing, Res.string.pricing, Modifier.onClick { it() }, NavLinkVariant)
                    Link(Res.link.downloads, Res.string.downloads, Modifier.onClick { it() }, NavLinkVariant)
                    Link(Res.link.contact, Res.string.contact, Modifier.onClick { it() }, NavLinkVariant)
                }
            ) {
                Link(Res.link.register, Res.string.get_started, variant = ButtonLinkVariant)
            }
            Column(
                PageStyle.toModifier().margin(top = 7.cssRem),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                content()
            }
        }
        Footer()
    }
}